import {
  EuiButtonIcon,
  EuiLink,
  EuiPopover,
  EuiText,
} from "@inscopix/ideas-eui";
import { DataTableColumnDefinition } from "../store/DataTableProvider.types";
import { useState } from "react";
import { useDataTableContext } from "../store/DataTableProvider";
import { captureException } from "@sentry/react";

interface ColumnHeaderBaseMetadatumLinkProps {
  columnDefinition: Extract<DataTableColumnDefinition, { kind: "metadatum" }>;
}

export const ColumnHeaderBaseMetadatumLink = ({
  columnDefinition,
}: ColumnHeaderBaseMetadatumLinkProps) => {
  const jumpToColumn = useDataTableContext((s) => s.jumpToColumn);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fileColumn = useDataTableContext(
    (s) => {
      const columnId = columnDefinition.file_column?.id;
      for (const table of s.tables) {
        for (const column of table.columns) {
          if (column.id === columnId) {
            return {
              id: column.id,
              name: column.name,
              tableId: table.id,
              tableName: table.name,
            };
          }
        }
      }
    },
    // Only recalculate if name is changed or column is deleted
    (a, b) => a?.name === b?.name,
  );

  // Display nothing if file column was deleted
  if (fileColumn === undefined) {
    return null;
  }

  const handleClick = async (options: {
    tableId: string;
    columnId: string;
  }) => {
    try {
      setIsLoading(true);
      await jumpToColumn({
        tableId: options.tableId,
        columnId: options.columnId,
      });
    } catch (error) {
      captureException(error);
    } finally {
      setIsLoading(false);
      setIsPopoverOpen(false);
    }
  };

  return (
    <EuiPopover
      button={
        <EuiButtonIcon
          aria-label="Join column source"
          color="primary"
          size="xs"
          iconSize="s"
          iconType="link"
          onClick={() => setIsPopoverOpen((isOpen) => !isOpen)}
        />
      }
      isOpen={isPopoverOpen}
      closePopover={() => setIsPopoverOpen(false)}
      anchorPosition="downCenter"
      ownFocus={false}
    >
      <div style={{ width: "300px" }}>
        <EuiText size="s">
          <p>
            Metadata in this column is read from the cells in the{" "}
            <EuiLink
              disabled={isLoading}
              onClick={() =>
                void handleClick({
                  tableId: fileColumn.tableId,
                  columnId: fileColumn.id,
                })
              }
            >
              {fileColumn.name}
            </EuiLink>{" "}
            column.
          </p>
        </EuiText>
      </div>
    </EuiPopover>
  );
};
